import type { FormEvent } from 'react';

export const REQUIRED_FIELD_MESSAGE = 'O campo precisa ser preenchido';
export const REQUIRED_VALIDATION_EVENT = 'app:validate-required';

export type RequiredValidationEventDetail = {
    form?: HTMLFormElement | null;
    invalidElements?: HTMLElement[];
};

export type RequiredValidationResult = {
    invalidElements: HTMLElement[];
    isValid: boolean;
};

export function validateRequiredFields(form?: HTMLFormElement | null): RequiredValidationResult {
    const formElement = form ?? null;
    const invalidElements: HTMLElement[] = [];

    if (formElement) {
        const requiredElements = Array.from(formElement.querySelectorAll<HTMLElement>('[required]'));

        for (const element of requiredElements) {
            if (!isEligibleForValidation(element)) continue;

            const asInput = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
            const validity = 'checkValidity' in asInput ? asInput.checkValidity() : true;
            let isInvalid = !validity;

            if (!isInvalid && isCnpjField(asInput)) {
                const digits = stripDigits(asInput.value || '');
                if (digits.length !== 0 && digits.length !== 14) {
                    isInvalid = true;
                }
            }

            if (isInvalid) {
                invalidElements.push(element);
            }
        }
    }

    const eventDetail: RequiredValidationEventDetail = { form: formElement, invalidElements };
    window.dispatchEvent(new CustomEvent<RequiredValidationEventDetail>(REQUIRED_VALIDATION_EVENT, { detail: eventDetail }));

    return { invalidElements, isValid: invalidElements.length === 0 };
}

export function buildFieldErrorsFromElements(invalidElements: HTMLElement[], message = REQUIRED_FIELD_MESSAGE): Partial<Record<string, string>> {
    const fieldErrors: Partial<Record<string, string>> = {};

    invalidElements.forEach((element) => {
        const name = (element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).name || element.getAttribute('name');
        if (name && !fieldErrors[name]) {
            fieldErrors[name] = message;
        }
    });

    return fieldErrors;
}

export function focusFirstInvalid(invalidElements: HTMLElement[]) {
    const first = invalidElements.find((el) => typeof el.focus === 'function');
    first?.focus?.();
}

function isEligibleForValidation(element: HTMLElement) {
    const asInput = element as HTMLInputElement;
    const isHiddenType = asInput.type === 'hidden' || asInput.getAttribute?.('type') === 'hidden';
    const isAriaHidden = asInput.getAttribute?.('aria-hidden') === 'true';
    const disabled = asInput.disabled || asInput.getAttribute?.('aria-disabled') === 'true';
    return !disabled && !isHiddenType && !isAriaHidden;
}

function isCnpjField(element: HTMLElement) {
    return element.getAttribute?.('data-cnpj') === 'true';
}

function stripDigits(value: string) {
    return value.replace(/\D/g, '');
}

export function resolveFormFromEvent(event?: FormEvent<HTMLFormElement>) {
    return event?.currentTarget ?? null;
}
